import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { Menu, Dropdown, Modal, Switch } from 'antd';
import {
  ExclamationCircleOutlined,
  FieldTimeOutlined,
  IdcardOutlined,
  LogoutOutlined,
  MoneyCollectOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  logout,
  selectLineOrAdmin,
  selectShowMoney,
  setShowMoney,
  setChangeRole,
} from 'features/auth/authSlice';
import authApi from 'api/authApi';
import MobileLogo from 'assets/icons/mobile-logo.svg';
import KingworkLogo from 'assets/icons/kingwork_logo.png';

import MyProfile from 'features/employee/pages/MyProfile';
import PasswordChange from 'features/auth/components/PasswordChange';
import { getEmployeeDetail, getEmployeeListApproved } from 'features/employee/employeeAction';
import { getDepartmentList } from 'features/department/departmentSlice';
import { getRoleList } from 'features/role/roleSlice';
import { getCategoryWithType, getLocations } from 'features/configuration/categoryAction';
import ListNotification from './ListNotifications';
import SelectLanguage, { languages } from './SelectLanguage';
import { isMobileOnly } from 'react-device-detect';
import { useHistory } from 'react-router-dom';
import DescriptionHolidayHeader from './DescriptionHolidayHeader';
import { getItem } from 'utils/storage';
import { IRole } from 'hrm-common/extensions/interfaces/personel';
import AvatarUser from 'components/AvatarBig';
import { getUser } from 'utils/auth';
import ModalConfigDateTime from './ModalConfigDateTime';
import { NotificationProvider } from 'features/notification';
import ModalConfigCurrency from './ModalConfigCurrency';
import { getNotifications, getNotificationsLineManager } from 'app/commonRedux/notificationSlice';
import { PayloadNotification } from 'components/interfaces/MenuListSidebar';
import { NotificationTitle } from 'hrm-common/extensions/enums/personel';
import { useAppSelector } from 'app/hooks';

const Container = styled.div`
  width: 100%;
  height: 45px;
  background-color: #fffefe;
  box-shadow: 0px 1px 10px rgba(22, 33, 46, 0.08);
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  position: fixed;
  z-index: 501;
  top: 0;
  padding: 0 16px;
  left: 0;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  order: ${(props: { pathName?: boolean }) => (props.pathName ? -1 : 0)};
  margin-right: ${(props: { pathName?: boolean }) => (props.pathName ? '16px' : '0')};

  img {
    height: 30px;
  }
`;

const RightContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const MobileContainer = styled.div`
  width: 100%;
  height: 3.5rem;
  background-color: white;
  box-shadow: 0px 1px 10px rgba(22, 33, 46, 0.08);
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-left: 15px;
`;

const Username = styled.div`
  font-family: Roboto;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  margin-left: 1rem;
  margin-right: 1rem;
`;

const IconContainer = styled.div`
  width: 30px;
  height: 30px;
  margin: 0 10px;
  border-radius: 50%;
  background-color: var(--cl_gray400);
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    box-shadow: 0 0 30px 0 rgba(126, 132, 143, 0.5) inset;
  }

  @media screen and (min-width: 1366px) {
    width: 35px;
    height: 35px;
  }
`;

const Icon = styled.svg`
  fill: white;
`;

const BellIcon = styled.svg`
  fill: white;
  width: 13px;

  @media screen and (min-width: 1366px) {
    width: 15px;
  }
`;

const Badge = styled.div`
  width: 20px;
  height: 15px;
  display: flex;
  border-radius: 35%;
  border: 1.5px solid white;
  background-color: #ff4c4c;
  position: absolute;
  justify-content: center;
  align-items: center;
  font-size: 8px;
  color: white;
  font-weight: bold;
  top: -2px;
  right: -10px;

  @media screen and (min-width: 1366px) {
    width: 22px;
    height: 17px;
    font-size: 10px;
    right: -12px;
    border: 2px solid white;
  }
`;

const BellIconContainer = styled.div`
  cursor: pointer;
  position: relative;
  margin: 0 10px;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    background-color: rgba(118, 124, 134, 0.1);

    ${Badge} {
      background-color: rgba(207, 37, 37, 1);
    }
  }

  @media screen and (min-width: 1366px) {
    width: 35px;
    height: 35px;
  }
`;

const ListNotificationsContainer = styled.div`
  border-radius: 9px;
  width: 30rem;
  background-color: white;
  box-shadow: 0px 10px 18px rgba(9, 30, 66, 0.15), 0px 0px 1px rgba(9, 30, 66, 0.31);
  position: absolute;
  top: 2.5rem;
  left: -25rem;
  z-index: 100;
`;

const Background = styled.div`
  width: 100vw;
  height: 100vh;
  position: absolute;
  top: 0px;
  left: 0px;
  z-index: 50;
`;

const IconWrapper = styled.div<{ padding?: string }>`
  display: flex;
  align-items: center;
  padding: ${(props) => props.padding};
  cursor: pointer;

  &:hover img {
    filter: brightness(0.8);
  }
`;

const filterDefault = {
  sortBy: 'status',
  orderBy: 'asc',
};

const Headers = ({ pathName }: any) => {
  const history = useHistory();
  const [isShowNotifications, setIsShowNotifications] = useState(false);
  const { t } = useTranslation(['header']);
  const dispatch = useDispatch();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isModalCurrencyVisible, setIsModalCurrencyVisible] = useState(false);
  const isShowMoney = useAppSelector(selectShowMoney);
  const isAdmin = useAppSelector(selectLineOrAdmin);

  const userName = useSelector((state: { auth: { user: { fullName: string } | null } }) =>
    state.auth.user ? state.auth.user.fullName : '',
  );
  const arrayWordFullName = userName.split(' ');
  const userId = useSelector((state: { auth: { user: { id: string } | null } }) =>
    state.auth.user ? state.auth.user.id : '',
  );
  const rolesUser = useSelector((state: { auth: { user: { roles: IRole[] } | null } }) =>
    state.auth.user ? state.auth.user.roles : [],
  );
  const isPassword = useSelector((state: { auth: { user: { isPassword: boolean } | null } }) =>
    state.auth.user?.isPassword ? state.auth.user.isPassword : false,
  );
  const badgeNumber = useSelector(
    (state: { notification: { badgeNumber: number } }) => state.notification.badgeNumber,
  );
  const language = languages.find((lang) => lang.code === getItem('language'));

  const [isVisibleMyProfile, setIsVisibleMyProfile] = useState(false);
  const [isVisiblePasswordChange, setIsVisiblePasswordChange] = useState(false);

  const [menuVisible, setMenuVisible]: any = useState(false);

  const [filterNoti, setFilterNoti]: PayloadNotification = useState({
    offset: 1,
    limit: 10,
    type: [],
  });
  const [filterNotiLineManager, setFilterNotiLineManager]: PayloadNotification = useState({
    offset: 1,
    limit: 10,
    type: [NotificationTitle.WATCHING_LIST, NotificationTitle.CONTRACT],
  });

  useEffect(() => {
    dispatch(
      getNotifications({
        userId: getUser().id,
        limit: filterNoti.limit,
        offset: filterNoti.offset,
        titles: filterNoti.type,
      }),
    );
  }, [filterNoti, dispatch]);

  useEffect(() => {
    dispatch(
      getNotificationsLineManager({
        userId: getUser().id,
        limit: filterNotiLineManager.limit,
        offset: filterNotiLineManager.offset,
        titles: filterNotiLineManager.type,
      }),
    );
  }, [filterNotiLineManager, dispatch]);

  useEffect(() => {
    if (userId) {
      // get location
      dispatch(getLocations({}));
      // Get dataset department
      dispatch(
        getDepartmentList({
          filters: filterDefault,
        }),
      );
      // Get dataset role
      dispatch(
        getRoleList({
          filters: filterDefault,
        }),
      );
      // Get dataset manager
      rolesUser?.every(({ name }: { name: string }) => name.toLowerCase() !== 'staff') &&
        dispatch(getEmployeeListApproved());

      // Get dataset onboard status
      dispatch(
        getCategoryWithType({
          typeConfig: 'onboard_status',
        }),
      );
      // Get dataset position
      dispatch(
        getCategoryWithType({
          typeConfig: 'position',
        }),
      );
      // Get dataset level
      dispatch(
        getCategoryWithType({
          typeConfig: 'level',
        }),
      );
      // Get dataset contract type
      dispatch(
        getCategoryWithType({
          typeConfig: 'contract_type',
        }),
      );
      // Get dataset gender
      dispatch(
        getCategoryWithType({
          typeConfig: 'gender',
        }),
      );
      // Get dataset marital status
      dispatch(
        getCategoryWithType({
          typeConfig: 'marital_status',
        }),
      );
      // Get dataset religion
      dispatch(
        getCategoryWithType({
          typeConfig: 'religion',
        }),
      );
      // Get dataset ethnicity
      dispatch(
        getCategoryWithType({
          typeConfig: 'ethnicity',
        }),
      );
      // Get dataset relationship
      dispatch(
        getCategoryWithType({
          typeConfig: 'relationship',
        }),
      );
      // Get dataset degree
      dispatch(
        getCategoryWithType({
          typeConfig: 'degree',
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLogout = () => {
    Modal.confirm({
      title: t('header:logout_modal_title'),
      icon: <ExclamationCircleOutlined />,
      content: '',
      okText: t('header:logout_modal_ok'),
      okType: 'danger',
      cancelText: t('header:logout_modal_cancel'),
      onOk: async () => {
        await authApi.logout(userId);
        dispatch(logout());
        localStorage.removeItem('current_date_choose');
      },
    });
  };

  const onClickShowMoney = () => {
    dispatch(setShowMoney(!isShowMoney));
  };

  const onClickChangeRole = () => {
    dispatch(setChangeRole(!isAdmin));
  };

  const onOpenMyProfile = () => {
    dispatch(getEmployeeDetail({ idItem: userId }));
    setIsVisibleMyProfile(true);
  };
  const menu = (
    <Menu
      style={{ borderRadius: '9px' }}
      onClick={(e: any) => {
        if (e.key !== 'language') {
          setMenuVisible(false);
        }
      }}
    >
      {!isMobileOnly && (
        <>
          <Menu.Item
            key="my-profile"
            onClick={onOpenMyProfile}
            icon={<IdcardOutlined style={{ fontSize: 16 }} />}
          >
            <div>{t('header:my_profile')}</div>
          </Menu.Item>
          <Menu.Item
            key="change-password"
            onClick={() => setIsVisiblePasswordChange(true)}
            icon={<UserSwitchOutlined style={{ fontSize: 16 }} />}
          >
            <div> {isPassword ? t('header:change_password') : t('header:create_password')}</div>
          </Menu.Item>
        </>
      )}
      {/* <Menu.Item
        key="formatDateTime"
        onClick={() => setIsModalVisible(true)}
        icon={<FieldTimeOutlined style={{ fontSize: 16 }} />}
      >
        <div>{t('header:format_date_time')}</div>
      </Menu.Item>
      {/* <Menu.Item
        key="formatCurrency"
        onClick={() => setIsModalCurrencyVisible(true)}
        icon={<FieldTimeOutlined style={{ fontSize: 16 }} />}
      >
        <div>Format currency</div>
      </Menu.Item> */}
      {getUser()?.permissions?.includes('SHOW_MONEY_IN_PAYROLL') && (
        <Menu.Item
          key="showModey"
          onClick={onClickShowMoney}
          icon={<MoneyCollectOutlined style={{ fontSize: 16 }} />}
        >
          <div>{!isShowMoney ? t('header:show_money') : t('header:hide_money')}</div>
        </Menu.Item>
      )}
      <Menu.Item key="logout" onClick={onLogout} icon={<LogoutOutlined style={{ fontSize: 16 }} />}>
        <div>{t('header:logout')}</div>
      </Menu.Item>
    </Menu>
  );

  const handleChangeFilterNoti = (value: any) => {
    setFilterNoti({ ...filterNoti, offset: value.offset, limit: value.limit, type: value.type });
  };
  const handleChangeFilterNotiLineManager = (value: any) => {
    setFilterNotiLineManager({
      ...filterNotiLineManager,
      offset: value.offset,
      limit: value.limit,
      type: value.type,
    });
  };

  if (!isMobileOnly) {
    return (
      <>
        <Container>
          <LogoContainer pathName={pathName}>
            <img src={KingworkLogo} alt="Company Logo" />
          </LogoContainer>
          <RightContainer>
            <DescriptionHolidayHeader />
            {(getUser()?.roles?.some((roles: any) => roles.id === 1) || false) && (
              <div style={{ margin: '0 10px', display: 'flex', alignItems: 'center' }}>
                <Switch
                  checked={!isAdmin}
                  onChange={onClickChangeRole}
                  checkedChildren="LM"
                  unCheckedChildren="Admin"
                  style={{
                    backgroundColor: isAdmin ? '#1890ff' : '#52c41a',
                    width: '65px',
                    height: '24px',
                  }}
                  className="custom-switch"
                />
              </div>
            )}
            {isShowNotifications && (
              <Background
                onClick={() => {
                  setIsShowNotifications(false);
                }}
              />
            )}
            {/* <Username title="username">{userName}</Username> */}
            <BellIconContainer>
              <IconWrapper
                onClick={() => {
                  setIsShowNotifications(true);
                }}
              >
                <IconContainer>
                  <BellIcon
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                  >
                    <path d="M8 16a2 2 0 0 0 2-2H6a2 2 0 0 0 2 2zm.995-14.901a1 1 0 1 0-1.99 0A5.002 5.002 0 0 0 3 6c0 1.098-.5 6-2 7h14c-1.5-1-2-5.902-2-7 0-2.42-1.72-4.44-4.005-4.901z" />
                  </BellIcon>
                </IconContainer>
              </IconWrapper>
              {badgeNumber !== 0 && (
                <Badge className="-mr-2">{badgeNumber > 99 ? '99+' : badgeNumber}</Badge>
              )}
              {isShowNotifications && (
                <NotificationProvider>
                  <ListNotificationsContainer>
                    <ListNotification
                      onToggle={(value: boolean) => setIsShowNotifications(value)}
                      onChangeFilters={(value: any) => handleChangeFilterNoti(value)}
                      onChangeFiltersLineManager={(value: any) =>
                        handleChangeFilterNotiLineManager(value)
                      }
                    />
                  </ListNotificationsContainer>
                </NotificationProvider>
              )}
            </BellIconContainer>
            <Dropdown
              overlayStyle={{ boxShadow: '0 0 4px rgb(0 0 0 / 20%)', borderRadius: '9px' }}
              overlay={menu}
              trigger={['click']}
              onVisibleChange={(flag: any) => setMenuVisible(flag)}
              visible={menuVisible}
            >
              <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <Username title="username">{userName}</Username>
                <AvatarUser
                  size={34}
                  fontSize={'14px'}
                  firstName={arrayWordFullName?.[arrayWordFullName.length - 1] || ''}
                  lastName={arrayWordFullName?.[0] || ''}
                />
              </div>
            </Dropdown>
            {/* <SelectLanguage style={{ marginLeft: 10 }} /> */}
            <div
              style={{
                padding: '10px 0',
                margin: '0 2em 0 1.3em',
              }}
            >
              <Dropdown
                overlayStyle={{ boxShadow: '0 0 4px rgb(0 0 0 / 20%)', borderRadius: '9px' }}
                overlay={<SelectLanguage />}
                trigger={['click']}
              >
                <IconWrapper padding="10px 0">
                  <img
                    style={{ boxShadow: '0 0 4px rgb(0 0 0 / 20%)' }}
                    alt={language?.code}
                    src={language?.src}
                    width={30}
                  />
                </IconWrapper>
              </Dropdown>
            </div>
          </RightContainer>
        </Container>
        <MyProfile isVisible={isVisibleMyProfile} onClose={() => setIsVisibleMyProfile(false)} />
        <ModalConfigDateTime visible={isModalVisible} setVisible={setIsModalVisible} />
        <ModalConfigCurrency
          visible={isModalCurrencyVisible}
          setVisible={setIsModalCurrencyVisible}
        />
        <PasswordChange
          isVisible={isVisiblePasswordChange}
          onClose={() => setIsVisiblePasswordChange(false)}
          isPassword={isPassword}
        />
      </>
    );
  } else {
    return (
      <>
        <MobileContainer>
          <img alt="MobileLogo" src={MobileLogo} onClick={() => history.push('/')} />
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {isShowNotifications && (
              <Background
                onClick={() => {
                  setIsShowNotifications(false);
                }}
              />
            )}
            <BellIconContainer>
              <IconWrapper>
                <BellIcon
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                  onClick={() => {
                    setIsShowNotifications(true);
                  }}
                >
                  <path d="M8 16a2 2 0 0 0 2-2H6a2 2 0 0 0 2 2zm.995-14.901a1 1 0 1 0-1.99 0A5.002 5.002 0 0 0 3 6c0 1.098-.5 6-2 7h14c-1.5-1-2-5.902-2-7 0-2.42-1.72-4.44-4.005-4.901z" />
                </BellIcon>
              </IconWrapper>
              {badgeNumber !== 0 && (
                <Badge className="-mr-2">{badgeNumber > 9 ? '9+' : badgeNumber}</Badge>
              )}
              {isShowNotifications && (
                <ListNotificationsContainer style={{ left: isMobileOnly ? '-13rem' : '-10rem' }}>
                  <ListNotification
                    onToggle={(value: boolean) => setIsShowNotifications(value)}
                    onChangeFilters={(value: any) => handleChangeFilterNoti(value)}
                    onChangeFiltersLineManager={(value: any) =>
                      handleChangeFilterNotiLineManager(value)
                    }
                  />
                </ListNotificationsContainer>
              )}
            </BellIconContainer>
            <Dropdown
              overlay={menu}
              trigger={['click']}
              onVisibleChange={(flag: any) => setMenuVisible(flag)}
              visible={menuVisible}
            >
              <IconWrapper padding="2px 0">
                <IconContainer style={{ marginLeft: 10, marginRight: 10 }}>
                  <Icon
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                  >
                    <path d="M3 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1H3zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
                  </Icon>
                </IconContainer>
              </IconWrapper>
            </Dropdown>
            <div
              style={{
                padding: '10px 0',
                margin: '0 20px 0 10px',
              }}
            >
              <Dropdown
                overlayStyle={{ boxShadow: '0 0 4px rgb(0 0 0 / 20%)', borderRadius: '9px' }}
                overlay={<SelectLanguage />}
                trigger={['click']}
              >
                <IconWrapper padding="10px 0">
                  <img
                    style={{ boxShadow: '0 0 4px rgb(0 0 0 / 20%)' }}
                    alt={language?.code}
                    src={language?.src}
                    width={30}
                  />
                </IconWrapper>
              </Dropdown>
            </div>
          </div>
        </MobileContainer>
      </>
    );
  }
};

export default Headers;
